import { useReadContract } from 'wagmi';
import { type Address } from 'viem';
import { STAKING_ABI } from '../constants';

/**
 * Preview position interface (from contract)
 */
export interface PreviewPosition {
  previewLockMultiplier: bigint;
  previewTotalMultiplier: bigint;
  previewWeight: bigint;
  previewDailyRewards: bigint;
  previewAPY: bigint;
}

/**
 * Custom hook to preview staking position details before committing
 * @param address - User's wallet address
 * @param amount - Amount to stake (in wei)
 * @param lockDays - Lock period in days
 * @param chainId - Current chain ID
 * @param requiredChainId - Dynamic chainId from backend config (BSC or Sepolia)
 * @param stakingContract - Dynamic staking contract address from backend config
 * @param enabled - Whether to enable the query
 */
export const usePreviewPosition = (
  address: Address | undefined,
  amount: bigint | undefined,
  lockDays: number,
  chainId: number | undefined,
  requiredChainId: number,
  stakingContract: Address,
  enabled: boolean = true
) => {
  const { data: previewData, refetch: refetchPreview } = useReadContract({
    address: stakingContract,
    abi: STAKING_ABI,
    functionName: 'previewPosition',
    args: address && amount && amount > 0n ? [address, amount, BigInt(lockDays)] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId && enabled && !!amount && amount > 0n,
      refetchInterval: 5000,
    },
  }) as { data: PreviewPosition | undefined; refetch: () => void };

  return {
    previewData,
    refetchPreview,
  };
};
